import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import VolumeUp from "@mui/icons-material/VolumeUp";

import { updateSection } from "../../../assignmentSlice";

import { getContentHeight } from "../../../../../utils/textUtils";
import {
  parseBlankQuestion,
  parseScrambleQuestion,
} from "../../../../../utils/parseUtils";
import { useTTS } from "../../../../../hooks/useTTS";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import ArrowButton from "../../widgets/ArrowButton";
import CorrectOverlay from "../../widgets/CorrectOverlay";
import StepsBar from "../../widgets/StepsBar";
import ScramblePage from "./ScramblePage";
import EngBlankPage from "./EngBlankPage";
import KorBlankPage from "./KorBlankPage";

const SentenceTestPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const { speak, stopSpeaking } = useTTS();

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const [selectedBlankIndex, setSelectedBlankIndex] = useState(0);
  const [userInput, setUserInput] = useState("");

  const currentPage = pageList[currentIndex] || { type: "", data: {} };
  const isDictation = section.question?.is_dictation ?? true;

  const completeSentence = (question, answer, type) => {
    if (type === "eng_blank") {
      const answerList = answer.split(" ");
      return question
        .replace(/_{2,}/g, () => {
          return answerList.shift() || "______";
        })
        .split(" / ")
        .join(" ");
    } else if (type === "eng_blank_random") {
      const answerList = answer.split(" / ");
      return question
        .replace(/_{2,}/g, () => {
          return answerList.shift() || "______";
        })
        .split(" / ")
        .join(" ");
    } else if (type === "eng_scramble") {
      return question.replace(/\*\*\((.*?)\)\*\*/, answer);
    }
  };

  const refineQuestionData = (data, type) => {
    if (type === "kor_blank") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            originalQuestion: element.question,
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_blank") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            answers: element.answer.split(" "),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_blank_random") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            answers: element.answer.split(" / "),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_scramble") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseScrambleQuestion(element.question),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    }
    return { data: data, type: type };
  };

  useEffect(() => {
    if (currentIndex < 0) return;

    if (pageList.length > 0 && isDictation) {
      stopSpeaking();
      speak({ text: currentPage.data.sentence });
    }
    if (!currentPage.isSubmitted) {
      setSelectedBlankIndex(0);
      setUserInput("");
    } else {
      setSelectedBlankIndex(-1);
      // isSubmitted가 true이면 firstAttemptAnswer를 보여줌
      if (currentPage.data.question) {
        const firstBlank = currentPage.data.question.find(q => q.type === "blank");
        if (firstBlank && firstBlank.firstAttemptAnswer) {
          setUserInput(firstBlank.firstAttemptAnswer);
        }
      }
    }
  }, [currentIndex]);

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    let tempPageList = [];

    for (let i = 0; i < section.question.data.length; i++) {
      const { type, data } = section.question.data[i];
      if (type === "kor_blank") {
        const korBlank = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...korBlank);
      } else if (type === "eng_blank") {
        const engBlank = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engBlank);
      } else if (type === "eng_blank_random") {
        const engBlankRandom = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engBlankRandom);
      } else if (type === "eng_scramble") {
        const engScramble = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engScramble);
      }
    }
    setPageList(tempPageList);
    setCurrentIndex(0);
  }, [section]);

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      const nextPage = pageList[currentIndex + 1];
      if (!nextPage.isSubmitted) {
        setSelectedBlankIndex(0);
      } else {
        setSelectedBlankIndex(-1);
      }
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        stopSpeaking();
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: {
                answer: pageList,
                is_finished: true,
              },
            })
          );
        }
        alert("학습을 완료하셨습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
    setUserInput("");
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevPage = pageList[currentIndex - 1];
      if (!prevPage.isSubmitted) {
        setSelectedBlankIndex(0);
      } else {
        setSelectedBlankIndex(-1);
      }
      setCurrentIndex(currentIndex - 1);
    } else {
      alert("더 이상 이전 문제가 없습니다.");
    }
    setUserInput("");
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: getContentHeight(),
          fontFamily:
            "'Pretendard','Spoqa Han Sans Neo','Apple SD Gothic Neo','Noto Sans KR',sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* ECHO 시안 13 — 단계 표시줄 */}
        <StepsBar />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative", // [화살표 v2] 하단 오버레이 기준점
            // [문장테스트 모바일] 40px 좌우 여백 축소 + 긴 문장이 위로 넘쳐
            // 상단바를 침범하던 것 → 세로 스크롤 컨테이너화
            padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "0 12px" : "0 40px", // [문장테스트 v5] 좌우 여유
            overflowY: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
          }}
        >
        <div
          style={{
            position: "absolute",
            right: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "80px",
            top: Math.min(window.innerWidth, window.screen.width) <= 768 ? "64px" : "110px", // [문장테스트 모바일] 스피커 버튼 위치
            padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "4px 8px" : "6px 12px",
            backgroundColor: "#FFFFFF", // ECHO — 흰 알약 + line 보더
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "1px solid #E6EDEC",
            borderRadius: "999px",
            boxShadow: "0 4px 14px rgba(14, 20, 27, 0.08)",
            cursor: "pointer",
          }}
          onClick={() => {
            stopSpeaking();
            speak({ text: currentPage.data.sentence });
          }}
        >
          <VolumeUp sx={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "20px" : "32px", color: "#00A396" }} />
        </div>
        {/* [화살표 v5] 모바일: 측면 숨김 → 하단바로 이동. PC: 기존 유지 */}
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <div>
        <ArrowButton
          direction="left"
          onClick={handlePrev}
          visibleCondition={currentIndex > 0}
        />
        </div>
        )}
        {/* 문제 표시 영역 */}
        <div
          style={{
            width: Math.min(window.innerWidth, window.screen.width) <= 768 ? "92%" : "80%",
            margin: Math.min(window.innerWidth, window.screen.width) <= 768 ? "0 auto" : 0, // [v10] 가운데 정렬
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            margin: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto 0" : 0, // 스크롤 시 상단 침범 방지
            padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "56px 0 90px" : 0, // 스피커/제출 버튼과 안 겹치게
            boxSizing: "border-box",
          }}
        >
          {currentPage.type.includes("kor_blank") && (
            <KorBlankPage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              userInput={userInput}
              selectedBlankIndex={selectedBlankIndex}
              setCurrentIndex={setCurrentIndex}
              setUserInput={setUserInput}
              setSelectedBlankIndex={setSelectedBlankIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
          {currentPage.type.includes("eng_blank") && (
            <EngBlankPage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              userInput={userInput}
              selectedBlankIndex={selectedBlankIndex}
              setCurrentIndex={setCurrentIndex}
              setUserInput={setUserInput}
              setSelectedBlankIndex={setSelectedBlankIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
          {currentPage.type.includes("scramble") && (
            <ScramblePage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
        </div>
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <div>
        <ArrowButton
          direction="right"
          onClick={handleNext}
          visibleCondition={true}
        />
        </div>
        )}
        </div>
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay
          bool={
            currentPage.isCorrect !== undefined
              ? currentPage.isCorrect
              : currentPage.isSubmitted
          }
        />
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "40px" : "70px", // [화살표 v6] CommonPage 카운터 바와 높이 통일
          width: "100vw",
          backgroundColor: "#F4F8F7", // ECHO --sage
          color: "#7A8490",
          fontWeight: 700,
          fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {(Math.min(window.innerWidth, window.screen.width) <= 768) ? (
          <>
            <div style={{ position: "absolute", left: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="left" onClick={handlePrev} visibleCondition={currentIndex > 0} />
            </div>
            <span>{currentIndex + 1} / {pageList.length}</span>
            <div style={{ position: "absolute", right: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="right" onClick={handleNext} visibleCondition={true} />
            </div>
          </>
        ) : (
          <span>{currentIndex + 1} / {pageList.length}</span>
        )}
      </div>
    </>
  );
};
export default SentenceTestPage;
